import React from "react"

import AdminApi from "utils/api/admin"
import UserApi from "utils/api/user"
import AffiliateApi from "utils/api/affiliate"
import { useHistory } from "react-router-dom"
import { CollectionContext } from "contexts/CollectionContext"
import { CaptionContext } from "contexts/CaptionContext"

import DownloadContext from "contexts/DownloadContext"
import { ClipboardContext } from "contexts/ClipboardContext"

export const AuthContext = React.createContext({
  validate: () => {},
  currentUser: null,
  logout: () => {},
  login: () => {},
  signup: () => {},
  updateUser: () => {},
  forgotPassword: () => {},
  resetPassword: () => {},
  subscribe: () => {},
})

export const AuthProvider = ({ children }) => {
  const history = useHistory()
  const { reset: resetDownloads } = React.useContext(DownloadContext)
  const { clearWords } = React.useContext(ClipboardContext)
  const { reset: resetCollections } = React.useContext(CollectionContext)
  const { reset: resetCaptions } = React.useContext(CaptionContext)
  const [currentUser, setCurrentUser] = React.useState(null)
  // Validating a certain admin
  const [isLoading, setIsLoading] = React.useState(false)
  const isPro = React.useMemo(() => !!currentUser && (currentUser.entitlements.pro || currentUser.entitlements.pro_plus), [currentUser])
  const rejectionMessage = { status: 401, response: { data: [{ message: "You do not have access to Pro features" }] } }

  //
  // Sign a user up
  //

  const signup = React.useCallback(
    params => {
      if (isLoading) return
      setIsLoading(true)

      return new Promise(async (resolve, reject) => {
        try {
          let signupResponse = await AdminApi.signup(params)
          setCurrentUser(signupResponse.data)
          resolve(signupResponse.data)
        } catch (err) {
          reject(err)
        } finally {
          setIsLoading(false)
        }
      })
    },
    [isLoading]
  )

  //
  // Log a user in
  //

  const login = React.useCallback(
    params => {
      if (isLoading) return
      setIsLoading(true)

      return new Promise(async (resolve, reject) => {
        try {
          let loginResponse = await AdminApi.login(params)
          setCurrentUser(loginResponse.data)
          resolve(loginResponse.data)
        } catch (err) {
          reject(err)
        } finally {
          setIsLoading(false)
        }
      })
    },
    [isLoading]
  )

  //
  // Log a user our and clear their current saved state
  //

  const logout = React.useCallback(() => {
    if (isLoading) return
    setIsLoading(true)

    return new Promise(async (resolve, reject) => {
      try {
        await AdminApi.logout()
      } finally {
        setCurrentUser(null)
        history.push("/login")
        setIsLoading(false)
      }
    })
  }, [history, isLoading])

  //
  // Validate our user and authenticate them if we need to
  //

  const validate = React.useCallback(() => {
    if (isLoading) return
    setIsLoading(true)

    return new Promise(async (resolve, reject) => {
      try {
        let validateResponse = await AdminApi.validate()
        // throw { response: { status: 123 }}
        setCurrentUser(validateResponse.data.data)
        resolve(validateResponse.data.data)
      } catch (err) {
        if (err.response.status === 401) {
          setCurrentUser(null)
        }
        reject(err)
      } finally {
        setIsLoading(false)
      }
    })
  }, [isLoading, logout])

  //
  // Validate our user and authenticate them if we need to
  //

  const updateUser = React.useCallback(params => {
    if (isLoading) return
    setIsLoading(true)

    return new Promise(async (resolve, reject) => {
      try {
        let updateResponse = await UserApi.update(params)
        // throw { response: { status: 123 }}
        setCurrentUser(updateResponse.data.data)
        resolve(updateResponse.data.data)
      } catch (err) {
        reject(err)
      } finally {
        setIsLoading(false)
      }
    })
  }, [isLoading])

  //
  // Forgot password
  //

  const forgotPassword = React.useCallback(
    params => {
      if (isLoading) return
      setIsLoading(true)

      return new Promise(async (resolve, reject) => {
        try {
          let forgotPasswordResponse = await AdminApi.forgotPassword(params)
          resolve(forgotPasswordResponse.data)
        } catch (err) {
          reject(err)
        } finally {
          setIsLoading(false)
        }
      })
    },
    [isLoading]
  )

  //
  // Reset password
  //

  const resetPassword = React.useCallback(
    params => {
      if (isLoading) return
      setIsLoading(true)

      return new Promise(async (resolve, reject) => {
        try {
          let resetPasswordResponse = await AdminApi.resetPassword(params)
          resolve(resetPasswordResponse.data)
        } catch (err) {
          reject(err)
        } finally {
          setIsLoading(false)
        }
      })
    },
    [isLoading]
  )

  //
  // Confirm affiliate email
  //

  const confirmAffiliate = React.useCallback(
    params => {
      if (isLoading) return
      setIsLoading(true)

      return new Promise(async (resolve, reject) => {
        try {
          let response = await AffiliateApi.confirm(params)
          resolve(response.data)
        } catch (err) {
          reject(err)
        } finally {
          setIsLoading(false)
        }
      })
    },
    [isLoading]
  )

  //
  // Subscribe a user to a plan
  //

  const subscribe = React.useCallback(
    params => {
      if (isLoading) return
      setIsLoading(true)

      return new Promise(async (resolve, reject) => {
        try {
          let subscribeResponse = await AdminApi.subscribe(params)
          setCurrentUser(subscribeResponse.data.data.user)
          resolve(subscribeResponse.data.data.user)
        } catch (err) {
          reject(err)
        } finally {
          setIsLoading(false)
        }
      })
    },
    [isLoading]
  )

  return (
    <AuthContext.Provider
      value={{
        currentUser,
        isPro,
        validate,
        login,
        logout,
        signup,
        updateUser,
        resetPassword,
        forgotPassword,
        rejectionMessage,
        subscribe,
        confirmAffiliate
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
